import { useEffect, useMemo, useRef, useState } from 'react'

import {
    displaySpeakerDescription,
    displaySpeakerName,
    getDisplaySpeakerData,
    canOpenSpeakerProfile,
} from './speakerHelper'
import { TextSnippetOutlined } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import useIsInViewport from './useIsInViewport';
import ProfilePicture from './ProfilePicture';
import { SPEAKER_ROW_PIC_SIZE } from '../constants/sizes';


const TranscriptPreview = ({
    // Passed in.
    curTime,
    onTranscriptClick,
    track,
}) => {

    const currentViewport = useRef();
    const isCurrentViewport = useIsInViewport(currentViewport);

    const [showLoading, setShowLoading] = useState(false);
    const message = useMemo(() => {
        const transcript = track.transcript || [];
        const time = Number.isFinite(curTime) ? curTime : 0;
        let start = 0;
        let end = transcript.length - 1;
        while (start <= end) {
            const middle = Math.floor((start + end) / 2);
            if (transcript[middle].timeSeconds <= time) start = middle + 1;
            else end = middle - 1;
        }
        return transcript[Math.max(0, end)] || null;
    }, [track.transcript, curTime]);

    useEffect(() => {
        if (showLoading) {
            // Call this here to show loading first for mobile devices where there is a delay between showing the full view.
            onTranscriptClick();
        }
    }, [showLoading, onTranscriptClick]);

    useEffect(() => {
        if (!isCurrentViewport) {
            setShowLoading(false);
        }
    }, [isCurrentViewport]);

    const openTranscript = () => {
        setShowLoading(true);
    }

    const speaker = message
        ? getDisplaySpeakerData(message.speakerId, track.speakers, message.anonymousDisplay)
        : null;
    const canOpenSpeaker = canOpenSpeakerProfile(speaker);
    const speakerDescription = speaker ? displaySpeakerDescription(speaker) : '';
    const speakerRowContent = speaker ? (
        <>
            <ProfilePicture
                speakerId={speaker.id}
                borderSize={1}
                imageSrc={speaker.imageURL}
                anonymousDisplay={speaker.anonymousDisplay}
                size={SPEAKER_ROW_PIC_SIZE}
                margin='0px 12px 0px 0px'
            />
            <span className={`trackDetailsTranscriptSpeakerText${speakerDescription ? '' : ' trackDetailsTranscriptSpeakerTextSingleLine'}`}>
                <span className='speakerName'>
                    {displaySpeakerName(speaker)}
                </span>
                {speakerDescription &&
                    <span className='speakerDescription'>
                        {speakerDescription}
                    </span>
                }
            </span>
        </>
    ) : null;


    return (
        <div
            ref={currentViewport}
            className='trackDetailsTranscriptCard'
        >
            <div className='trackDetailsTranscriptCardBody'>
                {showLoading ?
                    <div className='trackDetailsTranscriptState'>
                        <div className='trackDetailsTranscriptStateText'>
                            Loading transcript...
                        </div>
                        <div className="loadingSpinner"></div>
                    </div>
                    :
                    <div>
                        {message ?
                            <div className='trackDetailsTranscriptPreview'>
                                {speaker && (canOpenSpeaker ?
                                    <Link
                                        to={`/speaker/${encodeURIComponent(speaker.id)}`}
                                        className='trackDetailsTranscriptSpeakerRow'
                                        aria-label={`View ${displaySpeakerName(speaker)}`}
                                    >
                                        {speakerRowContent}
                                    </Link>
                                    :
                                    <div className='trackDetailsTranscriptSpeakerRow'>
                                        {speakerRowContent}
                                    </div>
                                )}

                                <button
                                    type='button'
                                    className='trackDetailsTranscriptOpenButton'
                                    onClick={openTranscript}
                                    aria-label='View full transcript'
                                >
                                    <span className='trackDetailsTranscriptMessageClip'>
                                        <span className='trackDetailsTranscriptMessage'>
                                            {message.message.map((word, wordIndex) => (
                                                <span
                                                    key={wordIndex}
                                                    className='message'
                                                    style={{
                                                        opacity: !!curTime && curTime + 0.4 < word.start ? 0.5 : 1.0
                                                    }}
                                                >
                                                    {word.text}
                                                </span>
                                            ))}
                                        </span>
                                    </span>
                                    <span className='trackDetailsTranscriptOpenCue'>
                                        <span>View full transcript</span>
                                    </span>
                                </button>
                            </div>

                            :
                            <button
                                type='button'
                                className='trackDetailsTranscriptEmptyButton'
                                onClick={openTranscript}
                                aria-label='View full transcript'
                            >
                                <span className='trackDetailsTranscriptState trackDetailsTranscriptEmptyState'>
                                    <span className='trackDetailsTranscriptEmptyIconShell'>
                                        <TextSnippetOutlined aria-hidden='true' style={{ fontSize: '32px', color: 'var(--app-text)' }} />
                                    </span>
                                    <span className='trackDetailsTranscriptStateTitle'>
                                        View full transcript
                                    </span>
                                </span>
                            </button>
                        }
                    </div>
                }
            </div>
        </div>
    );
};

export default TranscriptPreview;
